import { updateTask } from '@commenda/api';
import { pairHasOpenIssue, useTaskActivitiesForServiceRequest } from '@commenda/api/tasks';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { documentName, getPrintableDirectorName } from '@commenda/utils';
import { TSrPageTask } from '@commenda/types/src/service-request/types';
import { LegacyDocumentType, TaskStatus } from '@prisma/client';
import { ConfirmationModal } from '../../../../molecules/modals/ConfirmationModal';
import { useState } from 'react';
import { KeyedMutator } from 'swr';
import DocumentPreview from '../../../common/document-preview';
import { ReportIssueModal } from '../../../common/modals/ReportIssueModal';
import { WorkflowProcessDrawer } from '../../../common/workflow-process-drawer';
import { USCCORPIncorporationTasks } from '../config';

type DocSignModalProps = {
  open: boolean;
  onClose: () => void;
  task: TSrPageTask;
  serviceRequestId: string;
  onMarkTaskAsComplete: () => Promise<void>;
  serviceRequest: ServiceRequestDetails;
  mutate: KeyedMutator<unknown>;
};

export function DocumentVerificationModal({
  open,
  onClose,
  task,
  onMarkTaskAsComplete,
  serviceRequest,
  mutate,
}: DocSignModalProps) {
  const issueReportModalVisibility = useToggle();
  const confirmationModalVisibility = useToggle();

  const { data: activityFeed } = useTaskActivitiesForServiceRequest(
    serviceRequest?.id
  );
  const taskGroup = serviceRequest.taskGroups.find(
    (eachGrpTask) => eachGrpTask.id === task.taskGroupId
  );
  const clientUploadDocumentTask = taskGroup.tasks.find(
    (eachTask) =>
      eachTask.legacyServiceRequestTaskTaskId ===
      USCCORPIncorporationTasks.CLIENT_UPLOADS_SIGNED_SHAREHOLDING_DOCUMENTS
  );
  const taskHasOpenIssue = pairHasOpenIssue(
    activityFeed?.activities,
    clientUploadDocumentTask,
    task
  );

  function Header() {
    return (
      <div className="w-full flex justify-between mr-4 items-center">
        <h3 className="txt-large-medium text-text-secondary ">
          Review signed documents
        </h3>
        <div className="flex items-center gap-2">
          {task?.status !== TaskStatus.COMPLETED && (
            <>
              <Button
                size="sm"
                variant="destructive-tertiary"
                className="gap-3"
                onClick={issueReportModalVisibility.show}
              >
                <Icons.AlertTriangle className="size-5" />
                Report Issue
              </Button>
              {!taskHasOpenIssue ? (
                <Button
                  variant="primary"
                  size="sm"
                  className="txt-small-medium"
                  onClick={confirmationModalVisibility.show}
                >
                  <Icons.Check className="w-4 h-4" /> Verify
                </Button>
              ) : null}
            </>
          )}
        </div>
      </div>
    );
  }

  const corporationStakeholders =
    serviceRequest?.corporation?.corporationHasDirector || [];

  // Common documents for the corporation
  const USCCorpShareholdingDocuments = [LegacyDocumentType.BoardResolution];

  // Document types that need to be created for each director
  const USCCorpShareholdingIndividualDirectorDocuments = [
    LegacyDocumentType.SharePurchaseAndSaleAgreement,
    LegacyDocumentType.ShareCertificate,
  ];

  const corporationSteps = USCCorpShareholdingDocuments.map((eachDocument) => ({
    heading: documentName(eachDocument),
    key: eachDocument,
    description: '',

    children: (
      <DocumentPreview
        serviceRequestTaskId={task.id}
        serviceRequest={serviceRequest}
        mutate={mutate}
        documentType={eachDocument}
        viewOnly
      />
    ),
  }));

  const directorSteps = USCCorpShareholdingIndividualDirectorDocuments.flatMap(
    (documentType) =>
      corporationStakeholders.map((each) => {
        return {
          heading: `${documentName(documentType)} - ${getPrintableDirectorName({
            directorFirstName: each.director.directorFirstName,
            directorMiddleName: each.director.directorMiddleName,
            directorLastName: each.director.directorLastName,
          })}`,
          key: `${documentType}_${each?.director.id}`,
          description: '',

          children: (
            <DocumentPreview
              serviceRequestTaskId={task.id}
              serviceRequest={serviceRequest}
              mutate={mutate}
              documentType={documentType}
              directorId={each?.director?.id}
              viewOnly
            />
          ),
        };
      })
  );

  const steps = [...corporationSteps, ...directorSteps];

  const [activeParentStepId, setActiveParentStepId] = useState<string>(
    steps[0].key
  );

  return (
    <>
      <WorkflowProcessDrawer
        isShown={open}
        activeParentStepId={activeParentStepId}
        setActiveParentStepId={setActiveParentStepId}
        onClose={onClose}
        hideHeader
        header={<Header />}
        steps={steps}
      />
      <ReportIssueModal
        title="Let your client know where they need to make changes."
        description=""
        isOpen={issueReportModalVisibility.visible}
        setIsOpen={issueReportModalVisibility.hide}
        onReport={async () => {
          if (serviceRequest?.workflowExecutionId) {
            await updateTask({
              id: task.id,
              data: { status: TaskStatus.COMPLETED, isIssueReported: true },
            });
          } else {
            await updateTask({
              id: clientUploadDocumentTask.id,
              data: { status: TaskStatus.IN_PROGRESS },
            });
            await updateTask({
              id: task.id,
              data: { status: TaskStatus.IN_PROGRESS, isIssueReported: true },
            });
          }
          await mutate();
          issueReportModalVisibility.toggle();
          onClose();
        }}
        serviceRequest={serviceRequest}
      />
      <ConfirmationModal
        isOpen={confirmationModalVisibility.visible}
        onClose={confirmationModalVisibility.hide}
        onConfirm={() => {
          onMarkTaskAsComplete();
          confirmationModalVisibility.hide();
        }}
        title="Verify details"
        message="Please confirm that all details are accurate before proceeding. This action cannot be undone."
        hideCancelButton
      />
    </>
  );
}
